import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './components/DashboardHeader';
import DashboardSummary from './components/DashboardSummary';
import FilterControls from './components/FilterControls';
import RegistrationCard from './components/RegistrationCard';
import EmptyState from './components/EmptyState';
import { registrationService } from '../../services/registrationService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';


const RegistrationDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coachData, setCoachData] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  
  const [filters, setFilters] = useState({
    status: 'all',
    ageCategory: 'all',
    sortBy: 'newest',
  });

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const storedMobile = localStorage.getItem('userMobile');
    
    if (!authToken || !storedMobile) {
      navigate('/mobile-authentication');
      return;
    }

    loadRegistrations(storedMobile);
  }, [navigate]);

  // Refresh data when component comes into focus (user navigates back)
  // Debounced to prevent rapid refreshes and race conditions
  useEffect(() => {
    let timeoutId;
    let isRefreshing = false;
    
    const handleFocus = () => {
      // Clear any pending refresh
      clearTimeout(timeoutId);
      
      // Debounce to prevent rapid refreshes
      timeoutId = setTimeout(() => {
        // Prevent concurrent refreshes
        if (isRefreshing) return;
        
        const storedMobile = localStorage.getItem('userMobile');
        if (storedMobile) {
          isRefreshing = true;
          loadRegistrations(storedMobile).finally(() => {
            isRefreshing = false;
          });
        }
      }, 500); // Wait 500ms after focus before refreshing
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, registrations]);

  const loadRegistrations = async (mobileNumber) => {
    try {
      setLoading(true);
      setError('');

      const data = await registrationService?.getRegistrationsByMobile(mobileNumber);
      
      // Load players for each registration to calculate accurate completion percentage
      const registrationsWithPlayers = await Promise.all(
        data?.map(async (reg) => {
          const players = await registrationService?.getPlayersByRegistration(reg?.id);
          return {
            ...reg,
            players: players || []
          };
        }) || []
      );
      
      // Transform data to match UI format
      const transformedData = registrationsWithPlayers?.map(reg => ({
        id: reg?.registration_number || reg?.id, // Display ID
        dbId: reg?.id, // Database UUID for navigation
        ageCategory: formatAgeCategory(reg?.age_category),
        schoolName: reg?.schools?.name || 'Unknown School',
        coachName: reg?.poc_name,
        playerCount: reg?.players?.length || 0,
        status: reg?.status,
        completionPercentage: registrationService?.calculateCompletionPercentage(reg),
        createdDate: new Date(reg.created_at)?.toLocaleDateString('en-GB'),
        lastModified: new Date(reg.updated_at || reg.created_at)?.toLocaleDateString('en-GB'),
        declarationPdf: reg?.declaration_file_path
      })) || [];

      setRegistrations(transformedData);
      
      // Set coach data from first registration
      if (registrationsWithPlayers?.length > 0) {
        setCoachData({
          name: registrationsWithPlayers?.[0]?.poc_name,
          mobile: registrationsWithPlayers?.[0]?.mobile_number,
          email: registrationsWithPlayers?.[0]?.poc_email
        });
      }

    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err?.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...registrations];

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(reg => reg?.status === filters?.status);
    }

    if (filters?.ageCategory !== 'all') {
      filtered = filtered?.filter(reg => reg?.ageCategory === filters?.ageCategory);
    }

    switch (filters?.sortBy) {
      case 'newest':
        filtered?.sort((a, b) => new Date(b.lastModified.split('/').reverse().join('-')) - new Date(a.lastModified.split('/').reverse().join('-')));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a.lastModified.split('/').reverse().join('-')) - new Date(b.lastModified.split('/').reverse().join('-')));
        break;
      case 'completion':
        filtered?.sort((a, b) => b?.completionPercentage - a?.completionPercentage);
        break;
      default:
        break;
    }

    setFilteredRegistrations(filtered);
  };

  const formatAgeCategory = (category) => {
    const map = {
      'boys_u14': 'Boys U14',
      'girls_u15': 'Girls U15',
      'boys_u16': 'Boys U16'
    };
    return map?.[category] || category;
  };

  // Note: Completion percentage is now calculated using registrationService.calculateCompletionPercentage
  // which uses the same 6-item criteria as the declaration page checklist

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      ageCategory: 'all',
      sortBy: 'newest'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userMobile');
    navigate('/mobile-authentication');
  };

  const summaryData = {
    totalRegistrations: registrations?.length,
    draftCount: registrations?.filter(r => r?.status === 'draft')?.length,
    submittedCount: registrations?.filter(r => r?.status === 'submitted' || r?.status === 'pending')?.length,
    approvedCount: registrations?.filter(r => r?.status === 'approved')?.length,
    remainingSlots: Math.max(0, 3 - registrations?.length)
  };

  const hasActiveFilters = filters?.status !== 'all' || filters?.ageCategory !== 'all';
  const canCreateNew = registrations?.length < 3;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your registrations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error Loading Dashboard</p>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={() => {
            const storedMobile = localStorage.getItem('userMobile');
            if (storedMobile) loadRegistrations(storedMobile);
          }}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-6">
        <DashboardHeader
          coachName={coachData?.name || 'Coach'}
          canCreateNew={canCreateNew}
          onLogout={handleLogout}
        />

        <DashboardSummary summary={summaryData} />

        {/* LOOPEN SMS Notification Banner */}
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="MessageSquare" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>SMS Notifications:</strong> You will receive OTP and registration status updates (approval/rejection) via SMS from <span className="font-bold text-base text-primary">LOOPEN</span> short code. Please ensure your mobile number can receive messages from this sender.
            </p>
          </div>
        </div>

        <FilterControls
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {filteredRegistrations?.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {filteredRegistrations?.map(registration => (
              <RegistrationCard
                key={registration?.id}
                registration={registration}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            hasFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
          />
        )}
      </div>
    </div>
  );
};

export default RegistrationDashboard;